import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  DatatransPaymentErrorStatus,
  IPaymentBaseQueryParams,
  IPaymentErrorResult,
  IPaymentStatus,
  PaymentStatus,
  PaymentStatusQueryMap
} from 'libs/shared/src/models';
import { BehaviorSubject } from 'rxjs';
import { BasicPaymentProvider } from '../basic.pp';

const datatransErrorMap: Partial<Record<DatatransPaymentErrorStatus, string>> = {
  card_invalid: 'paymentService.errors.datatrans.card_invalid',
  declined: 'paymentService.errors.datatrans.declined',
  soft_declined: 'paymentService.errors.datatrans.soft_declined',
  incomplete: 'paymentService.errors.datatrans.incomplete',
  timeout: 'paymentService.errors.datatrans.timeout',
  invalid_setup: 'paymentService.errors.datatrans.invalid_setup',
  secure_authentication: 'paymentService.errors.datatrans.secure_authentication',
  error_on_start: 'paymentService.errors.datatrans.error_on_start',
  referral: 'paymentService.errors.datatrans.referral',
  error: 'paymentService.errors.datatrans.error'
};

interface IDatatransQueryParams extends IPaymentBaseQueryParams {
  /** The Datatrans order ID */
  orderID: string;
  /** The Datatrans transaction ID */
  datatransTrxId: string;
}

@Injectable({
  providedIn: 'root'
})
export class DatatransPaymentProvider extends BasicPaymentProvider {
  // Services
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);

  // Properties & Signals
  /** Emits the result received from the payment provider */
  public override resultReceived: BehaviorSubject<IPaymentStatus | undefined> = new BehaviorSubject<IPaymentStatus | undefined>(undefined);

  // Methods
  /**
   * Initializes datatrans payment provider and will check for a redirect back from datatrans
   */
  override initializeProvider(): void {
    this.checkForRedirectBack();
  }

  /**
   * Opens a new window at datatrans using the given transactionToken
   * @param transactionToken to use for identification
   * @param redirectUrl optional redirect url to use after payment
   */
  override initializePayment(transactionToken?: string, redirectUrl?: string): void {
    if (!redirectUrl || !transactionToken || transactionToken.length === 0) return;
    window.open(redirectUrl, '_self');
  }

  /**
   * Generates a user-friendly message based on the payment status and optional error result
   * @param status of the payment
   * @param result optional error result from the payment provider
   */
  override generateStatusMessage(status: IPaymentStatus, result?: IPaymentErrorResult<DatatransPaymentErrorStatus>): string | undefined {
    if (status.status === PaymentStatus.Failed) {
      const mappedError = result?.error ? datatransErrorMap[result.error] : undefined;
      if (mappedError) return this.translateService.instant(mappedError);
      return this.translateService.instant('paymentService.errors.generic');
    }
    return undefined;
  }

  /**
   * Checks if the current route has a redirect back from datatrans and emits the result
   */
  private checkForRedirectBack(): void {
    const urlParams = this.route.snapshot.queryParams as IDatatransQueryParams;
    if (urlParams.datatransTrxId && urlParams.state && urlParams.orderID) {
      this.resultReceived.next({
        transactionId: urlParams.datatransTrxId,
        status: PaymentStatusQueryMap[urlParams.state]
      });
    }
  }
}
